import Cta from "@components/Cta/Cta";
import Grid from "@components/Grid/Grid";
import {
  NextContentfulImage,
  NextContentfulLoaderProps,
} from "@components/Image";
import Link from "@components/Link/Link";
import { CourseTypeSetting } from "@customTypes/CourseTypeSettings";
import { FacultyMember } from "@customTypes/FacultyMember";
import { ProgrammeDetails } from "@customTypes/programmeDetails";
import { useDevice } from "@utilities/react/get-device/get-device";
import clsx from "clsx";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import styles from "./C282_Electives.module.scss";

const AccordionContainer = dynamic(
  () => import("@components/C11_VerticalAccordion/AccordionContainer")
);

export interface Course extends CourseTypeSetting {
  courseType: string;
  courseList: Array<{
    courseTitle: string;
    courseDescription: string;
    facultyMembers?: FacultyMember[] | undefined;
    programmeList: ProgrammeDetails[] | undefined;
  }>;
}

interface ElectiveItemProps {
  index: number;
  course: Course;
  isOpen?: boolean;
  closeAll: () => void;
  openStates: boolean[];
  handleOpenStateChange: (index: number, v: boolean) => void;
  singleDisplay: boolean;
  displayFacultyList: boolean;
}

const loaderConfig: NextContentfulLoaderProps = {
  fit: "fill",
  f: "faces",
  h: 60,
};

export const ElectiveItem = (props: ElectiveItemProps) => {
  const {
    index,
    course,
    closeAll,
    openStates,
    handleOpenStateChange,
    singleDisplay,
    displayFacultyList,
  } = props;
  const [lock, setLock] = useState(false);
  const [height, setHeight] = useState("auto");
  const outterContentRef: any = useRef();
  const innerContentRef: any = useRef();
  const device = useDevice();
  const isOpen = openStates[index];

  const getLinks = (programmeList: ProgrammeDetails[]) =>
    programmeList.map((programme) => ({
      linkText: programme.programmeTitle,
      url: programme.slug,
    }));

  const performOpen = () => {
    outterContentRef.current.visibility = "visible";
    outterContentRef.current.style.height =
      innerContentRef.current.offsetHeight + "px";
    setTimeout(() => {
      outterContentRef.current.style.height = "auto";
    }, 300);
  };

  const performClose = () => {
    outterContentRef.current.style.height =
      innerContentRef.current.offsetHeight + "px";
    setTimeout(() => {
      outterContentRef.current.style.height = 0;
    }, 0);
    setTimeout(() => {
      outterContentRef.current.visibility = "hidden";
    }, 300);
  };

  const handleToggle = (event) => {
    event.preventDefault();
    if (!lock) {
      setLock(true);
      setTimeout(() => {
        setLock(false);
      }, 300);

      if (isOpen) {
        handleOpenStateChange(index, false);
      } else {
        if (singleDisplay) closeAll();
        handleOpenStateChange(index, true);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      performOpen();
    } else {
      performClose();
    }
  }, [openStates]);

  const applyHeight = () => {
    if (["desktop", "desktop-large"].includes(device)) {
      const items = document.querySelectorAll(
        ".item"
      ) as NodeListOf<HTMLElement>;
      const heights = [...items].map((item) => item.offsetHeight);
      const maxHeight = Math.max(...heights);
      setHeight(maxHeight + "px");
    }
  };

  useEffect(applyHeight, [device, course]);
  return (
    <li key={course.courseTypeTitle}>
      <div
        className={clsx(styles.item, "item", isOpen && styles["is--active"])}
      >
        <div className={styles["elective-details"]}>
          <h3 className={styles.title}>{course.courseTypeTitle}</h3>
          <p>{course.courseTypeDescription}</p>
          <button
            className={styles["toggle-action"]}
            aria-expanded={isOpen}
            onClick={handleToggle}
          >
            <span className={styles["show-more"]}>{course.accordionText}</span>
            <span className={styles["show-less"]}>Show less</span>
          </button>
        </div>
        <div
          ref={outterContentRef}
          aria-hidden={!isOpen}
          className={clsx(styles["elective-accordion-drop"])}
        >
          <div ref={innerContentRef} className={styles["elective-content"]}>
            <AccordionContainer
              noHeading
              accordionFields={{
                singleDisplay: false,
                defaultOpen: false,
                accordionItems: course.courseList.map((courseDetails) => ({
                  fields: {
                    title: courseDetails.courseTitle,
                    content: (
                      <Grid row>
                        <Grid column sm={12} md={8}>
                          {courseDetails?.courseDescription && (
                            <div
                              dangerouslySetInnerHTML={{
                                __html: courseDetails.courseDescription,
                              }}
                            ></div>
                          )}
                          {!!courseDetails?.programmeList &&
                            !!courseDetails?.programmeList.length && (
                              <>
                                <h5 className={styles["sub-title"]}>
                                  {course.courseProgrammeHeaderText}
                                </h5>
                                <ul className={styles["styled-list"]}>
                                  {getLinks(courseDetails?.programmeList).map(
                                    (link, i) => (
                                      <li key={link.url + link.linkText + i}>
                                        <Cta
                                          aria-expanded="false"
                                          type="tertiary"
                                          url={link.url}
                                          linkText={link.linkText || ""}
                                          icon="icon-arrow"
                                          customClass={styles["cu-text"]}
                                        />
                                      </li>
                                    )
                                  )}
                                </ul>
                              </>
                            )}
                        </Grid>
                        {displayFacultyList &&
                          courseDetails.facultyMembers &&
                          courseDetails.facultyMembers.length > 0 && (
                            <Grid column sm={12} md={4}>
                              <div className={styles["elective-profile"]}>
                                <h5
                                  className={styles["elective-profile-title"]}
                                >
                                  {course.courseFacultyHeaderText}
                                </h5>
                                <ul className={styles["elective-profile-list"]}>
                                  {courseDetails.facultyMembers.map(
                                    (facultyMember, index) => (
                                      <li
                                        key={`${facultyMember.profileFirstname}${index}`}
                                      >
                                        <div
                                          className={styles["profile-image"]}
                                        >
                                          <Link href={facultyMember.slug || ""}>
                                            <a href={facultyMember.slug || ""}>
                                              {facultyMember?.profilePhoto && (
                                                <NextContentfulImage
                                                  loaderConfig={loaderConfig}
                                                  alt={
                                                    facultyMember.profilePhoto
                                                      .altText
                                                  }
                                                  src={
                                                    facultyMember.profilePhoto
                                                      .url
                                                  }
                                                  width={60}
                                                  height={60}
                                                />
                                              )}
                                            </a>
                                          </Link>
                                        </div>
                                        <div className={styles["profile-name"]}>
                                          <Link href={facultyMember.slug || ""}>
                                            <a>
                                              {facultyMember.profileFirstname}{" "}
                                              {facultyMember.profileSurname}
                                            </a>
                                          </Link>
                                        </div>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            </Grid>
                          )}
                      </Grid>
                    ),
                  },
                })),
              }}
            />
          </div>
        </div>
        <a
          href="#"
          aria-expanded={isOpen}
          onClick={handleToggle}
          className={clsx(
            styles["toggle-action"],
            styles["toggle-action--mobile"]
          )}
        >
          <span className={styles["show-less"]}>Show less</span>
        </a>
      </div>
    </li>
  );
};

export default ElectiveItem;
